import { useEffect, useRef, useState } from "react";
import { OpenSheetMusicDisplay } from "opensheetmusicdisplay";
import QuestionLayout from "./QuestionLayout";
import { rhythmCountPosition } from "./rhythmCountGeometry";

/* ───────────────────────────────────────────── */

export default function RhythmCountWidget({
  config,
  value = {},
  onChange,
  mode,
  feedback
}) {
  const containerRef = useRef(null);
  const osmdRef = useRef(null);
  const overlayLayerRef = useRef(null);
  const initializedRef = useRef(false);
  const valueRef = useRef(value);

  const [activeNoteId, setActiveNoteId] = useState(null);
  const [palettePos, setPalettePos] = useState(null);

  /* ───────── VALUE REF ───────── */

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    if (!initializedRef.current) return;
    renderValueOverlays();
  }, [mode, feedback, activeNoteId, value]);

  /* ───────── OSMD INIT (ONCE) ───────── */

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const osmd = new OpenSheetMusicDisplay(containerRef.current, {
      drawTitle: false,
      drawSubtitle: false,
      drawComposer: false,
      drawLyricist: false,
      drawPartNames: false,
      drawMeasureNumbers: false,
      autoResize: false
    });

    osmdRef.current = osmd;

    (async () => {
      await osmd.load(config.musicXML);
      osmd.EngravingRules.MinimumDistanceBetweenSystems = Math.max(
        osmd.EngravingRules.MinimumDistanceBetweenSystems || 0, 12
      );
      osmd.render();

      await new Promise(r =>
        requestAnimationFrame(() =>
          requestAnimationFrame(r)
        )
      );

      ensureOverlayLayer();
      bindNoteClicks();
      renderValueOverlays();
    })();
  }, []);

  /* ───────── SVG OVERLAY LAYER ───────── */

  function ensureOverlayLayer() {
    const svg = containerRef.current?.querySelector("svg");
    if (!svg) return;

    const rootG = svg.querySelector("g");
    if (!rootG) return;

    let layer = rootG.querySelector("#rhythm-value-layer");
    if (!layer) {
      layer = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "g"
      );
      layer.setAttribute("id", "rhythm-value-layer");
      rootG.appendChild(layer);
    }

    overlayLayerRef.current = layer;
  }

  function clearOverlayLayer() {
    if (overlayLayerRef.current) {
      overlayLayerRef.current.innerHTML = "";
    }
  }

  /* ───────── NOTE CLICK BINDING ───────── */

  function bindNoteClicks() {
    if (mode === "review") return;

    const osmd = osmdRef.current;
    if (!osmd) return;

    let globalMeasureIndex = 0;

    osmd.GraphicSheet.MusicPages.forEach(page => {
      page.musicSystems.forEach(system => {
        system.graphicalMeasures.forEach(measureGroup => {
          const measure = measureGroup[0];
          if (!measure) return;

          const m = globalMeasureIndex++;

          measure.staffEntries.forEach((staffEntry, s) => {
            staffEntry.graphicalVoiceEntries.forEach((gve, v) => {
              gve.notes.forEach((note, n) => {
                const el = note.vfnote?.[0]?.attrs?.el;
                if (!el) return;

                const id = makeNoteId(m, s, v, n);
                //console.log(id);
                el.style.cursor = "pointer";

                el.onclick = e => {
                  e.stopPropagation();

                  const rect = el.getBoundingClientRect();
                  const containerRect =
                    containerRef.current.getBoundingClientRect();

                  setActiveNoteId(id);
                  setPalettePos({
                    x: rect.left - containerRect.left,
                    y: rect.top - containerRect.top - 30
                  });
                };
              });
            });
          });
        });
      });
    });
  }

  /* ───────── TEXT MEASUREMENT ───────── */

  function measureTextWidth(text, font = "14px sans-serif") {
    const canvas =
      measureTextWidth._canvas ||
      (measureTextWidth._canvas = document.createElement("canvas"));
    const ctx = canvas.getContext("2d");
    ctx.font = font;
    return ctx.measureText(text).width;
  }

  /* ───────── DRAW VALUE OVERLAYS ───────── */

  function renderValueOverlays() {
    const osmd = osmdRef.current;
    const layer = overlayLayerRef.current;
    const svg = containerRef.current?.querySelector("svg");
    if (!osmd || !layer || !svg) return;

    clearOverlayLayer();
    svg.style.overflow = "visible";
    const isReview = mode === "review";

    let globalMeasureIndex = 0;

    osmd.GraphicSheet.MusicPages.forEach(page => {
      page.musicSystems.forEach(system => {
        system.graphicalMeasures.forEach(measureGroup => {
          const measure = measureGroup[0];
          if (!measure) return;

          const m = globalMeasureIndex++;

          measure.staffEntries.forEach((staffEntry, s) => {
            staffEntry.graphicalVoiceEntries.forEach((gve, v) => {
              gve.notes.forEach((note, n) => {
                const el = note.vfnote?.[0]?.attrs?.el;
                if (!el) return;

                const position = rhythmCountPosition(note.vfnote[0], el, layer);
                if (!position) return;
                const id = makeNoteId(m, s, v, n);
                drawValueBox(position.x, position.y, id, isReview);
              });
            });
          });
        });
      });
    });
  }

  /* ───────── VALUE DISPLAY ───────── */

  function drawValueBox(x, y, id, isReview) {
    const part = feedback?.parts?.[id];
    const display = valueRef.current?.[id] || "";

    if (!display && !isReview && id !== activeNoteId) return;

    const padding = 6;
    const textWidth = measureTextWidth(display || " ");
    const boxWidth = Math.max(10, textWidth) + padding * 2;

    const fo = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "foreignObject"
    );

    fo.setAttribute("x", x - boxWidth / 2);
    fo.setAttribute("y", y);
    fo.setAttribute("width", boxWidth);
    fo.setAttribute("height", 25);
    fo.style.pointerEvents = "none";

    const div = document.createElement("div");
    div.textContent = display;
    div.style.padding = `0 ${padding}px`;
    div.style.fontSize = "14px";
    div.style.whiteSpace = "nowrap";
    div.style.textAlign = "center";
    div.style.boxSizing = "border-box";
    div.style.height = "25px";
    div.style.border = "2px solid transparent";

    if (id === activeNoteId && !isReview) {
      div.style.border = "2px solid #1976d2";
      div.style.borderRadius = "4px";
    }

    if (isReview && part) {
      div.style.border = "2px solid";
      div.style.borderRadius = "4px";
      div.style.borderColor = part.correct
        ? "#2e7d32"
        : "#c62828";
    }

    fo.appendChild(div);
    overlayLayerRef.current.appendChild(fo);
  }

  /* ───────── NOTE ID ───────── */

  function makeNoteId(m, s, v, n) {
    return `m${m}-s${s}-v${v}-n${n}`;
  }

  /* ───────── RENDER ───────── */

  return (
    <QuestionLayout
      number={config.number}
      prompt={config.prompt}
      points={config.points}
      mode={mode}
      feedback={feedback}
    >
      <div
        ref={containerRef}
        style={{
          position: "relative",
          maxWidth: "100%",
          overflowX: "visible"
        }}
      />

      {/* ───── PALETTE (IMMEDIATE COMMIT) ───── */}
      {activeNoteId && palettePos && mode !== "review" && (
        <div
          style={{
            position: "absolute",
            left: palettePos.x,
            top: 10,
            display: "flex",
            alignItems: "center",
            gap: "6px",
            background: "#f8f8f8",
            border: "1px solid #aaa",
            borderRadius: "6px",
            zIndex: 1000
          }}
        >
          {config.palette.map(sym => (
            <button
              key={sym}
              style={{ width: 28, display: 'flex', justifyContent: 'center' }}
              onClick={() => {
                const next = (valueRef.current?.[activeNoteId] || "") + sym;
                valueRef.current = {
                  ...(valueRef.current || {}),
                  [activeNoteId]: next
                };
                onChange(valueRef.current);
              }}
            >
              {sym}
            </button>
          ))}

          <button
            onClick={() => {
              valueRef.current = {
                ...(valueRef.current || {}),
                [activeNoteId]: ""
              };
              onChange(valueRef.current);
            }}
          >
            Clear
          </button>
        </div>
      )}
    </QuestionLayout>
  );
}
